import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Typography,
  Grid,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import { requestLogin } from "../../redux/actions/userActionCreators";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";
import GoogleSignInButton from "../../assets/googleButton/GoogleSignInButton";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const loading = useSelector((state) => state.feedback.loading) > 0;
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [redirectUrl, setRedirectUrl] = useState("");

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    const redirect = location.search.match(/redirect=(.+)/);
    if (redirect?.length) {
      setRedirectUrl(redirect[1]);
    }
  }, [location]);

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      identifier: "",
      password: "",
    },
    validationSchema: Yup.object({
      identifier: Yup.string()
        .email(t("valid_email"))
        .max(255)
        .required(t("required_field")),
      password: Yup.string().max(255).required(t("required_field")),
    }),
    onSubmit: (values) => {
      dispatch(
        requestLogin(false, values.identifier, values.password, redirectUrl),
      );
    },
  });

  const login = useGoogleLogin({
    onSuccess: (codeResponse) =>
      dispatch(requestLogin(true, "", "", redirectUrl, codeResponse, history)),
    flow: "auth-code",
  });

  return (
    <>
      <Box>
        <h1>{t("log_in")}</h1>
      </Box>
      <br></br>
      <Box component="main">
        <form onSubmit={formik.handleSubmit}>
          <div style={{ height: "130px" }}>
            <Typography variant="subtitle2">{t("email_address")}</Typography>
            <TextField
              error={Boolean(
                formik.touched.identifier && formik.errors.identifier,
              )}
              fullWidth
              helperText={formik.touched.identifier && formik.errors.identifier}
              margin="normal"
              name="identifier"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.identifier}
              placeholder={t("type_email")}
            />
          </div>
          <div style={{ height: "120px" }}>
            <Typography variant="subtitle2">{t("password")}</Typography>

            <TextField
              error={Boolean(formik.touched.password && formik.errors.password)}
              fullWidth
              helperText={formik.touched.password && formik.errors.password}
              margin="normal"
              name="password"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              placeholder={t("type_password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div style={{ height: "30px", textAlign: "end" }}>
            <Link to="/forgot-password">{t("forgot_password")}?</Link>
          </div>
          <Box sx={{ py: 2 }}>
            <Button
              color="secondary"
              disabled={loading}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              {t("login")}
            </Button>
          </Box>
        </form>

        <Grid container alignItems="center" mt={1} mb={1}>
          <Grid item xs={5}>
            <hr id="line" />
          </Grid>

          <Grid item xs={2} align="center">
            <Typography variant="subtitle2">{t("or")}</Typography>
          </Grid>
          <Grid item xs={5}>
            <hr id="line" />
          </Grid>
        </Grid>
        <Box sx={{ py: 2 }}>
          <GoogleSignInButton login={login} />
        </Box>
      </Box>
    </>
  );
};

export default Login;
